import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AIAssistantDrawer from "@/components/AIAssistantDrawer";
import NotificationBell from "@/components/NotificationBell";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import UpgradeModal from "@/components/UpgradeModal";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import TrialBanner from "@/components/TrialBanner";
import { CopilotProvider } from "@/context/CopilotContext";
import CopilotDrawer, { CopilotLauncher } from "@/components/CopilotDrawer";
import TopBarTimer from "@/components/TopBarTimer";
import {
  LayoutDashboard,
  Users,
  KanbanSquare,
  CalendarDays,
  LayoutTemplate,
  Sparkles,
  LogOut,
  Zap,
  Mail,
  Inbox as InboxIcon,
  Receipt,
  Timer,
  Settings as SettingsIcon,
  ShieldAlert,
  CreditCard,
  FileText,
  Hammer,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/contacts", label: "Contacts", icon: Users, testId: "nav-contacts" },
  { to: "/inbox", label: "Inbox", icon: InboxIcon, testId: "nav-inbox" },
  { to: "/outbox", label: "Outbox", icon: Mail, testId: "nav-outbox" },
  { to: "/pipelines", label: "Pipelines", icon: KanbanSquare, testId: "nav-pipelines" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/funnels", label: "Funnels", icon: LayoutTemplate, testId: "nav-funnels" },
  { to: "/automations", label: "Automations", icon: Zap, testId: "nav-automations" },
  { to: "/time", label: "Time", icon: Timer, testId: "nav-time" },
  { to: "/expenses", label: "Expenses", icon: Receipt, testId: "nav-expenses" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiContext, setAiContext] = useState("");
  const [inboxUnread, setInboxUnread] = useState(0);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("peak:sidebar_collapsed") === "1"; }
    catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("peak:sidebar_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const loadInboxUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox/unread-count");
      setInboxUnread(data?.count || 0);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadInboxUnread();
    const t = setInterval(loadInboxUnread, 30000);
    // Also refresh when the tab regains focus
    const onFocus = () => loadInboxUnread();
    window.addEventListener("focus", onFocus);
    window.addEventListener("peak:inbox-read", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("peak:inbox-read", onFocus);
    };
  }, [loadInboxUnread]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <CopilotProvider>
    <div className="peak-canvas min-h-screen flex text-[var(--peak-ink)]">
      {/* Sidebar */}
      <aside
        className={`shrink-0 border-r border-[var(--peak-border)] bg-[var(--peak-bg)] flex flex-col transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`}
        data-testid="sidebar"
        data-collapsed={collapsed ? "1" : "0"}
      >
        <div className={`border-b border-[var(--peak-border)] flex items-center gap-3 ${collapsed ? "px-3 py-6 justify-center" : "px-6 py-6 justify-between"}`}>
          <div className="flex-1 min-w-0">
            <WorkspaceSwitcher collapsed={collapsed} />
          </div>
          {!collapsed && (
            <button
              onClick={toggleCollapsed}
              data-testid="sidebar-collapse-btn"
              title="Collapse sidebar"
              className="p-1.5 border border-transparent hover:border-[var(--peak-ink)] hover:bg-white transition-colors flex-none"
            >
              <ChevronsLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
          )}
        </div>

        {collapsed && (
          <button
            onClick={toggleCollapsed}
            data-testid="sidebar-expand-btn"
            title="Expand sidebar"
            className="mx-3 mt-3 h-8 flex items-center justify-center border border-[var(--peak-border)] bg-white hover:bg-[var(--peak-ink)] hover:text-white transition-colors"
          >
            <ChevronsRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}

        <nav className="flex-1 px-3 py-4 flex flex-col gap-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const showBadge = item.to === "/inbox" && inboxUnread > 0;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 border border-transparent text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-[var(--peak-ink)] text-[var(--peak-bg)] peak-glow"
                      : "text-[var(--peak-ink)] hover:bg-white hover:border-[var(--peak-ink)]"
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-none" strokeWidth={2.5} />
                {!collapsed && (
                  <span className="uppercase tracking-wider text-xs">{item.label}</span>
                )}
                {showBadge && !collapsed && (
                  <span
                    data-testid="inbox-unread-badge"
                    className="ml-auto bg-[var(--peak-accent)] text-white text-[10px] font-mono px-1.5 py-0.5 min-w-[20px] text-center"
                  >
                    {inboxUnread > 99 ? "99+" : inboxUnread}
                  </span>
                )}
                {showBadge && collapsed && (
                  <span
                    className="absolute ml-6 -mt-4 bg-[var(--peak-accent)] text-white text-[9px] font-mono px-1 min-w-[16px] text-center"
                  >
                    {inboxUnread > 9 ? "9+" : inboxUnread}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-[var(--peak-border)]">
          {!collapsed && <NotificationBell onRefresh={loadInboxUnread} />}
          <button
            data-testid="open-ai-drawer-btn"
            onClick={() => { setAiContext(""); setAiOpen(true); }}
            title={collapsed ? "AI Copilot" : undefined}
            className={`mt-2 w-full flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 bg-[var(--peak-blue-deep)] text-white text-xs font-bold uppercase tracking-wider hover:bg-[var(--peak-ink)] transition-colors`}
          >
            <Sparkles className="w-4 h-4 flex-none" />
            {!collapsed && "AI Copilot"}
          </button>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            title={collapsed ? "Settings" : undefined}
            className={`mt-2 flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--peak-muted)] hover:text-[var(--peak-ink)]`}
          >
            <SettingsIcon className="w-4 h-4 flex-none" /> {!collapsed && "Settings"}
          </NavLink>
          <NavLink
            to="/settings/billing"
            data-testid="nav-billing"
            title={collapsed ? "Billing" : undefined}
            className={({ isActive }) =>
              `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 text-xs font-semibold uppercase tracking-wider ${
                isActive ? "text-[var(--peak-accent)]" : "text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
              }`
            }
          >
            <CreditCard className="w-4 h-4 flex-none" /> {!collapsed && "Billing"}
          </NavLink>
          {user?.is_superadmin && (
            <>
              <NavLink
                to="/superadmin"
                data-testid="nav-superadmin"
                end
                title={collapsed ? "Super Admin" : undefined}
                className={({ isActive }) =>
                  `mt-2 flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 text-xs font-bold uppercase tracking-wider border ${
                    isActive
                      ? "bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] border-[var(--peak-accent)]"
                      : "text-[var(--peak-accent)] border-[var(--peak-accent)] hover:bg-[var(--peak-accent)] hover:text-[var(--peak-accent-contrast)]"
                  }`
                }
              >
                <ShieldAlert className="w-4 h-4 flex-none" /> {!collapsed && "Super Admin"}
              </NavLink>
              <NavLink
                to="/superadmin/propgen"
                data-testid="nav-propgen"
                title={collapsed ? "PropGen" : undefined}
                className={({ isActive }) =>
                  `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 text-xs font-semibold uppercase tracking-wider ${
                    isActive ? "text-[var(--peak-accent)]" : "text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
                  }`
                }
              >
                <FileText className="w-4 h-4 flex-none" /> {!collapsed && "PropGen"}
              </NavLink>
              <NavLink
                to="/superadmin/development"
                data-testid="nav-development"
                title={collapsed ? "Development" : undefined}
                className={({ isActive }) =>
                  `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 text-xs font-semibold uppercase tracking-wider ${
                    isActive ? "text-[var(--peak-accent)]" : "text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
                  }`
                }
              >
                <Hammer className="w-4 h-4 flex-none" /> {!collapsed && "Development"}
              </NavLink>
            </>
          )}
        </div>

        <div className={`border-t border-[var(--peak-border)] flex items-center ${collapsed ? "px-2 py-3 justify-center flex-col gap-2" : "px-3 py-4 gap-3"}`}>
          <div className="w-9 h-9 bg-[var(--peak-ink)] text-white flex items-center justify-center font-heading font-bold text-sm flex-none">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate" data-testid="user-name">{user?.name || user?.email}</div>
              <div className="section-label truncate">{user?.role}</div>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="p-2 hover:bg-[var(--peak-ink)] hover:text-white transition-colors flex-none"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden relative">
        {/* Floating persistent timer — always visible in the top-right corner */}
        <div className="sticky top-0 z-30 flex justify-end px-4 py-2 pointer-events-none">
          <div className="pointer-events-auto">
            <TopBarTimer />
          </div>
        </div>
        <TrialBanner />
        <ImpersonationBanner />
        {children}
      </main>

      {/* Copilot lives inside the flex row so opening it PUSHES content left */}
      <CopilotDrawer />

      <AIAssistantDrawer
        open={aiOpen}
        onOpenChange={setAiOpen}
        initialContext={aiContext}
      />
      <UpgradeModal />
      <CopilotLauncher />
    </div>
    </CopilotProvider>
  );
}
